// React binding for the session seam. One fetch on mount; sign-in is a
// redirect to forge-auth's hosted login, sign-out a best-effort POST that
// flips local state to signed-out regardless of the network outcome.

import { useCallback, useEffect, useState } from 'react';
import {
  type SessionState,
  fetchSessionUser,
  getAuthBaseUrl,
  postSignOut,
  signInUrl,
} from './session.js';

export interface UseSession {
  readonly state: SessionState;
  /** False when VITE_AUTH_URL is unset — the Sign in button is disabled. */
  readonly configured: boolean;
  readonly signIn: () => void;
  readonly signOut: () => Promise<void>;
}

export function useSession(baseUrl: string | null = getAuthBaseUrl()): UseSession {
  const [state, setState] = useState<SessionState>(() =>
    baseUrl ? { status: 'loading' } : { status: 'signed-out' },
  );

  useEffect(() => {
    if (!baseUrl) return;
    let cancelled = false;
    void fetchSessionUser(baseUrl).then((user) => {
      if (cancelled) return;
      setState(user ? { status: 'signed-in', user } : { status: 'signed-out' });
    });
    return () => {
      cancelled = true;
    };
  }, [baseUrl]);

  const signIn = useCallback(() => {
    if (!baseUrl) return;
    window.location.assign(signInUrl(baseUrl, window.location.href));
  }, [baseUrl]);

  const signOut = useCallback(async () => {
    await postSignOut(baseUrl);
    setState({ status: 'signed-out' });
  }, [baseUrl]);

  return { state, configured: baseUrl !== null, signIn, signOut };
}
